import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ReadingProfile {
  id: string;
  user_id: string;
  profile_summary: string;
  total_books_analyzed: number;
  generated_at: string;
  last_books_hash: string | null;
}

export const useReadingProfile = () => {
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['readingProfile'],
    queryFn: async (): Promise<ReadingProfile | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_reading_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        console.error('Error fetching reading profile:', error);
        throw error;
      }

      return data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const generateProfile = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('generate-reading-profile', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['readingProfile'] });
      queryClient.invalidateQueries({ queryKey: ['awardBookRecommendations'] });
      toast.success(`Profile updated from ${data.booksAnalyzed} books!`);
    },
    onError: (error: Error) => {
      console.error('Error generating profile:', error);
      toast.error(error.message || 'Failed to generate reading profile');
    },
  });

  return {
    profile,
    isLoading,
    generateProfile: generateProfile.mutate,
    isGenerating: generateProfile.isPending,
  };
};